<template>
  <section class="customer-profile-purchases">
    <AM3DataList
      v-model:sort="dataListSort"
      v-model:selected="selectedPurchases"
      :rows="computedPurchases"
      :columns="dataListColumns"
      :loading="isFetchingPurchases && computedPurchases.length === 0"
      :options="{
        search: 'inside',
        searchPlaceholder: 'Search orders',
        sortable: true,
        itemVocabulary: ['purchase', 'purchases'],
        checkboxes: false,
      }"
      row-key="fanEventOrderOid"
      :count="totalCount"
      data-test-id="customer-purchases-datalist"
      @row-click="handleDataListRowClick"
      @search="handleDataListSearch"
    >
      <template #after>
        <AM3Pagination
          v-if="totalPages > 1"
          v-model="currentPage"
          :total-pages="totalPages"
          :page-size="pageSize"
          :page-size-options="PURCHASES_PAGE_SIZE_OPTIONS"
          :loading="isFetchingPurchases"
          dictionary="Purchases"
          @change="handlePageChange"
          @update:page-size="handlePageSizeChange"
        />
      </template>
      <!-- Custom cell template for Description -->
      <template #cell-description="{ row }">
        <div class="flex items-center gap-3 w-full">
          <img
            v-if="row.imageUrl"
            :src="row.imageUrl"
            :alt="row.description"
            loading="lazy"
            class="w-12 h-12 rounded-lg flex-shrink-0"
          >
          <div
            v-else
            class="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0"
          >
            <AM3Icon
              name="calendar"
              class="w-6 h-6 text-gray-400"
            />
          </div>
          <div class="flex-1 min-w-0">
            <div class="font-medium text-primary">
              {{ formatTicketDescription(row) }}
            </div>
            <div class="text-sm text-secondary truncate">
              {{ row.description }}
            </div>
          </div>
        </div>
      </template>

      <!-- Custom cell template for Attribution -->
      <template #cell-source="{ row }">
        <div
          v-if="row.source"
          class="flex flex-row items-center"
        >
          <div
            :class="row.source.platform === 'CSV Import' ? 'bg-surfaces-neutral' : 'bg-brand-light text-brand' "
            class="outline-surface-light p-2 size-8 rounded-full mr-3 flex flex-row items-center justify-center"
          >
            <AM3Icon
              :type="row.source.platform === 'CSV Import' ? 'arrow-down-tray' : 'ticket'"
              size="sm"
            />
          </div>
          <span class="inline-block font-medium">
            {{ row.source && row.source.platform ? row.source.platform : 'Unknown' }}
          </span>
        </div>
      </template>

      <!-- Custom cell template for Order amount with proper formatting -->
      <template #cell-grossTotal="{ row }">
        <AM3Formatter
          :value="row.grossTotal"
          type="currency"
          :options="{ code: row.currencyCode }"
        />
      </template>

      <template #cell-purchaseDateTime="{ row }">
        <AM3Formatter
          v-tooltip.bottom="formatDateTime(row.purchaseDateTime, { dateStyle: 'medium', timeStyle: 'short' })"
          :value="row.purchaseDateTime"
          type="relative-time"
        />
      </template>

      <!-- Custom cell template for Order amount with proper formatting -->
      <template
        #cell-status="{ row }"
      >
        <AM3Tag
          class="capitalize"
          :rounded="false"
          dot
          :type="row.status"
          :label="row.status"
        />
      </template>

      <template #empty>
        <div
          v-if="isFetchingPurchases"
          class="flex justify-center items-center my-12"
        >
          <AM3Loading
            size="lg"
            color="brand"
          />
          <div class="mt-4 text-center text-lg font-medium">
            Loading purchases...
          </div>
        </div>

        <div
          v-else
          class=" my-12  [&>div]:space-y-8"
        >
          <AM3EmptyState
            heading="No purchases found"
            description="Check back later"
          />
        </div>
      </template>
    </AM3DataList>

    <!-- Order Details Modal -->
    <OrderDetailsModal
      :is-open="modalIsOpen"
      :modal-data="modalData"
      @close="handleClosePurchaseModal"
    />
  </section>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import OrderDetailsModal from './components/OrderDetailsModal.vue'
import AM3DataList from '@/components/am3-generic/data/AM3DataList.vue'
import AM3Pagination from '@/components/am3-generic/data/AM3Pagination.vue'
import AM3Tag from '@/components/am3-generic/base/AM3Tag.vue'
import AM3Loading from '@/components/am3-generic/base/AM3Loading.vue'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import { dummyPurchaseData } from '@/utils/audience'
import { usePaginatedFetch } from '@/composables/usePaginatedFetch'
import type { RootState } from '@/store/modules/types'
import AM3EmptyState from '@/components/am3/empty-state/AM3EmptyState.vue'
import AM3Formatter from '@/components/am3-generic/data/AM3Formatter.vue'
import { formatDateTime } from '@/utils/formatters'

import type { DataListColumn } from '@/components/am3-generic/data/AM3DataList.vue'
import imageDefault from '@/assets/images/placeholders/image-default.png'

// Refs
const searchQuery = ref('')
const searchQueryModel = ref('')
const modalIsOpen = ref(false)
const isOpeningModal = ref(false)
const modalData = ref(null)
const orderBy = ref({ key: 'purchaseDateTime', order: 'desc' })
const selectedPurchases = ref([])
const dataListSort = ref({ key: 'purchaseDateTime', order: 'desc' })

const store = useStore<RootState>()
const route = useRoute()

// Vuex access
const customer = computed(() => store.state.customerModule.customer)
const customerState = computed(() => store.state.customerModule)
const isFetchingPurchases = computed(() => store.state.customerModule.isFetchingPurchases)
const currentCampaign = computed(() => store.state.campaignModule.currentCampaign)
const currentEvent = computed(() => store.state.eventModule.currentEvent)
const isFeatureEnabled = computed(() => store.getters['authModule/isFeatureEnabled'])

const fetchAndUpdatePurchaseImages = async () => {
  const purchases = computedPurchases.value
  if (!purchases.length) return

  // Only fetch images for first 50 purchases to avoid fetching 200+ campaigns/events
  const purchasesToProcess = purchases.slice(0, 50)

  // Collect unique campaign and event OIDs (limit to 50 max to avoid huge requests)
  const campaignOids = [...new Set(
    purchasesToProcess
      .filter(p => p.campaignOid && !p.imageUrl)
      .map(p => p.campaignOid),
  )].slice(0, 50)

  const eventOids = [...new Set(
    purchasesToProcess
      .filter(p => p.eventOid && !p.imageUrl)
      .map(p => p.eventOid),
  )].slice(0, 50)

  // Batch fetch campaigns and events
  const [campaignData, eventData] = await Promise.all([
    campaignOids.length > 0
      ? store.dispatch('customerModule/FETCH_CAMPAIGNS_BY_OID_AND_SEARCH', { oids: campaignOids })
      : Promise.resolve([]),
    eventOids.length > 0
      ? store.dispatch('customerModule/FETCH_EVENTS_BY_OID_AND_SEARCH', { oids: eventOids })
      : Promise.resolve([]),
  ])

  // Extract rows from response (handle both array and {rows: []} formats)
  const campaigns = Array.isArray(campaignData) ? campaignData : (campaignData?.rows || [])
  const events = Array.isArray(eventData) ? eventData : (eventData?.rows || [])

  // Create image lookup maps
  const campaignImages = new Map()
  campaigns.forEach((campaign) => {
    if (campaign.resources?.length > 0) {
      const imageResource = campaign.resources.find(r => r.assetType === 'campaign-image')
      if (imageResource) {
        campaignImages.set(campaign.oid, imageResource.url)
      }
    }
  })

  const eventImages = new Map()
  events.forEach((event) => {
    if (event.resources?.length > 0) {
      const imageResource = event.resources.find(r => r.assetType === 'event-image')
      if (imageResource) {
        eventImages.set(event.oid, imageResource.url)
      }
    }
  })

  // Update only the processed purchases with images
  purchasesToProcess.forEach((purchase) => {
    if (purchase.imageUrl) return

    // Try campaign image first, then event image
    purchase.imageUrl
      = (purchase.campaignOid && campaignImages.get(purchase.campaignOid))
        || (purchase.eventOid && eventImages.get(purchase.eventOid))
        || null
  })
}

const formatTicketDescription = (row: any) => {
  const quantity = row.quantity || 1
  const ticketType = getTicketTypeFromLineItems(row)
  return `${quantity}x ${ticketType}`
}

const getTicketTypeFromLineItems = (row: any) => {
  if (row.lineItems && row.lineItems.length > 0) {
    // If multiple line items, show the first one
    return row.lineItems[0].ticketType || 'General Admission'
  }
  return 'General Admission'
}

const isCustomerProfileFeatureEnabled = computed(() =>
  isFeatureEnabled.value(['customer-profiles']),
)

const computedPurchases = computed(() => {
  if (!isCustomerProfileFeatureEnabled.value) return dummyPurchaseData()
  return customerState.value.fanPurchases.filter(item => item.fanEventOrderOid != null)
})

// The feature-disabled dummy-data path has no backend count to page against
const totalCount = computed(() =>
  isCustomerProfileFeatureEnabled.value ? customerState.value.totalPurchasesCount : computedPurchases.value.length,
)

interface PurchaseRow {
  description: string
  source: string
  grossTotal: number
  // The order's own currency, which wins over the promoter's region symbol.
  // Served as `currencyCode` — the resource column is `:currency-code`.
  currencyCode?: string
  orderId: string
  status: string
  purchaseDateTime: string
  format: object
}

const dataListColumns = computed<DataListColumn<PurchaseRow>[]>(() => [
  {
    key: 'description',
    title: 'Description',
    freeze: true,
    width: 476,
    sortable: false,
  },
  {
    key: 'source',
    title: 'Ticketing platform',
    width: 180,
    sortable: true,
  },
  {
    key: 'grossTotal',
    title: 'Order amount',
    width: 160,
    sortable: true,
    align: 'right',
    format: {
      type: 'currency',
      options: {
        currency: 'USD',
      },
    },
  },
  {
    key: 'orderId',
    title: 'Order ID',
    width: 130,
    sortable: false,
    align: 'right',
  },
  {
    key: 'status',
    title: 'Status',
    width: 110,
    sortable: true,
    align: 'right',
  },
  {
    key: 'purchaseDateTime',
    title: 'Order date',
    width: 140,
    sortable: true,
    align: 'right',
    format: {
      type: 'datetime',
      options: {
        format: 'MMM D, YYYY h:mm A',
      },
    },
  },
])

// Methods
const formatCurrency = (value: any) => {
  if (value === undefined || value === null || isNaN(parseFloat(value))) {
    return '$0.00'
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value)
}

const PURCHASES_PAGE_SIZE_OPTIONS = [25, 50, 100]

// Map UI field names to API field names
const orderByFieldMapping: Record<string, string> = {
  purchaseDateTime: 'orderDate',
  source: 'provider',
  grossTotal: 'totalCost',
  orderId: 'orderId',
  status: 'status',
  // description is not sortable by backend (complex mapping)
}

const {
  currentPage,
  pageSize,
  totalPages,
  fetch: fetchPurchases,
  resetToFirstPage,
  handlePageChange,
  handlePageSizeChange,
} = usePaginatedFetch({
  totalCount,
  pageSizeOptions: PURCHASES_PAGE_SIZE_OPTIONS,
  defaultPageSize: 50,
  pageSizeStorageKey: 'ar_customer_purchases_page_size',
  // FETCH_CUSTOMER_PURCHASES silently drops a call made while one is already in
  // flight (rather than queuing it) - isFetching lets usePaginatedFetch defer and
  // replay instead of firing a call the store would just drop.
  isFetching: isFetchingPurchases,
  fetchRows: async ({ top, skip }) => {
    const apiFieldName = orderByFieldMapping[orderBy.value.key] || orderBy.value.key
    const orderByValue = `${apiFieldName} ${orderBy.value.order}`

    await store.dispatch('customerModule/FETCH_CUSTOMER_PURCHASES', {
      top,
      skip,
      searchString: searchQuery.value,
      orderby: orderByValue,
      fanOid: route.params.customer,
    })
  },
})

const handleSearch = async () => {
  searchQuery.value = searchQueryModel.value
  resetToFirstPage()
  await fetchPurchases()
}

const handleDataListSearch = (query: string) => {
  searchQueryModel.value = query
  handleSearch()
}

const handleDataListRowClick = (row: any) => {
  handleOpenPurchaseModal(row)
}

const handleOpenPurchaseModal = async (purchaseData: any) => {
  if (isOpeningModal.value) return
  if (!purchaseData) {
    modalIsOpen.value = false
    isOpeningModal.value = false
    return
  }
  isOpeningModal.value = true

  // Use already-fetched image from batch fetch, or default placeholder
  const fallbackImage = purchaseData.imageUrl || imageDefault

  modalData.value = {
    ...purchaseData,
    fallbackImage,
  }
  modalIsOpen.value = true
  isOpeningModal.value = false
}

const handleClosePurchaseModal = () => {
  modalIsOpen.value = false
}

// Watches
watch(customer, (val, oldVal) => {
  if (val && val.oid && !oldVal && !isFetchingPurchases.value && isCustomerProfileFeatureEnabled.value) {
    fetchPurchases()
  }
}, { immediate: true })

watch(dataListSort, (val) => {
  if (val) {
    orderBy.value = {
      key: val.key,
      order: val.order,
    }
    resetToFirstPage()
    fetchPurchases()
  }
}, { deep: true })

// Batch fetch images for all purchases (only 2 API requests total)
watch(computedPurchases, (newPurchases) => {
  if (newPurchases.length > 0) {
    fetchAndUpdatePurchaseImages()
  }
}, { immediate: true })

// Lifecycle hooks
onMounted(() => {
  store.commit('customerModule/RESET_CUSTOMER_PURCHASES')
})

onBeforeUnmount(() => {
  store.commit('customerModule/RESET_CUSTOMER_PURCHASES')
  store.commit('campaignModule/RESET_CURRENT_CAMPAIGN')
  store.commit('eventModule/RESET_CURRENT_EVENT')
})
</script>
